// ============================================================================
// cycle-engine/engine.js — the ONLY surface components use to read the live
// round and to trigger round/cycle transitions.
//
// This is the thin I/O layer: it calls the Stage 2/4 SECURITY DEFINER RPCs and
// reads the rounds table. All PURE turn/funding logic lives in
// shared/utils/roundState.js (re-exported from ./index.js). Components must NOT
// call supabase for round/cycle mutations directly — only through here — so the
// authorization + audit + atomicity guarantees always come from the RPCs.
// ============================================================================

import { supabaseClient } from '../../shared/utils/supabaseClient';

// Round statuses that mean "in progress" (there is at most one per group).
const LIVE_STATUSES = ['open', 'funded', 'paid_out'];

/** Unwrap a supabase rpc/query result, throwing a readable error on failure. */
function unwrap({ data, error }, action) {
  if (error) {
    // Surface the RPC's RAISE message (e.g. 'not authorized…', 'round not funded…').
    throw new Error(error.message || `Failed to ${action}.`);
  }
  return data;
}

// ---- reads ----------------------------------------------------------------

/**
 * The group's live round (open/funded/paid_out), or null if none is in progress.
 * @param {number|string} groupId
 */
export async function getCurrentRound(groupId) {
  const res = await supabaseClient
    .from('rounds')
    .select('*')
    .eq('group_id', groupId)
    .in('status', LIVE_STATUSES)
    .order('cycle_number', { ascending: false })
    .order('round_number', { ascending: true })
    .limit(1)
    .maybeSingle();
  return unwrap(res, 'load the current round');
}

/**
 * All rounds for a cycle (for the rotation timeline). If cycleNumber is omitted,
 * returns every round for the group (caller can group by cycle_number).
 */
export async function getCycleRounds(groupId, cycleNumber) {
  let query = supabaseClient.from('rounds').select('*').eq('group_id', groupId);
  if (cycleNumber != null) query = query.eq('cycle_number', cycleNumber);
  const res = await query
    .order('cycle_number', { ascending: true })
    .order('round_number', { ascending: true });
  return unwrap(res, 'load the cycle rounds');
}

// ---- transitions (all via SECURITY DEFINER RPCs) --------------------------

/**
 * Seed cycle 1 / open round 1 for a group (admin only), with a chosen collection
 * order and schedule. The RPC stamps each round's scheduled_collection_date /
 * payment_due_date server-side from (firstCollectionDate + frequency).
 * @param {number} groupId
 * @param {{ collectorOrder?: number[], firstCollectionDate?: string, frequency?: string }} [opts]
 *   collectorOrder = active hand_ids in collection order (null => by position);
 *   firstCollectionDate = 'YYYY-MM-DD'; frequency = 'weekly'|'bi-weekly'|'monthly'.
 */
export async function openFirstRound(groupId, { collectorOrder = null, firstCollectionDate = null, frequency = null } = {}) {
  return unwrap(
    await supabaseClient.rpc('open_first_round', {
      p_group_id: groupId,
      p_collector_order: collectorOrder,
      p_first_collection_date: firstCollectionDate,
      p_frequency: frequency,
    }),
    'open the first round'
  );
}

/** Send the live round's payout to its collector (admin only). */
export async function sendPayout(handId, amount) {
  return unwrap(
    await supabaseClient.rpc('send_payout', { p_hand_id: handId, p_amount: amount }),
    'send the payout'
  );
}

/**
 * PRIMARY advance path: the collector confirms receipt, which advances the round.
 * (collector-authorized RPC — unchanged from Stage 2.)
 */
export async function confirmReceipt(payoutId) {
  return unwrap(
    await supabaseClient.rpc('confirm_receipt_and_advance', { p_payout_id: payoutId }),
    'confirm receipt'
  );
}

/**
 * FALLBACK advance path: the admin advances a stuck round when the collector
 * won't/can't confirm. Recorded as an admin override, NEVER as a collector
 * confirmation. Blocked by the RPC if the payout is disputed.
 * @param {number} payoutId
 * @param {string} [note] optional reason -> override_note
 */
export async function adminAdvanceRound(payoutId, note = null) {
  return unwrap(
    await supabaseClient.rpc('admin_advance_round', { p_payout_id: payoutId, p_note: note }),
    'advance the round'
  );
}

/**
 * Begin a fresh rotation once the current cycle is complete (admin only), with a
 * chosen collection order and schedule (same shape as openFirstRound). The RPC's
 * completion guard rejects this if the current cycle still has a live/unconfirmed round.
 * @param {number} groupId
 * @param {{ collectorOrder?: number[], firstCollectionDate?: string, frequency?: string }} [opts]
 */
export async function startNewCycle(groupId, { collectorOrder = null, firstCollectionDate = null, frequency = null } = {}) {
  return unwrap(
    await supabaseClient.rpc('start_new_cycle', {
      p_group_id: groupId,
      p_collector_order: collectorOrder,
      p_first_collection_date: firstCollectionDate,
      p_frequency: frequency,
    }),
    'start a new cycle'
  );
}
